import React, { useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Search } from "lucide-react";
import {
  AddCircleOutlineOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import Close from "@mui/icons-material/Close";
import { EditIcon } from "../comps/CustomIcons";
import AddUnitModal from "../components/item_master/modal/unit-modal";
import { useGetApi } from "../hooks/useGetApi";
import { getUnitApi, deleteUnitByIdApi } from "../services/unit.service";
import { toast } from "react-toastify";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import UnitSettingModal from "../components/item_master/modal/unit-setting-modal";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const MasterUnit = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("unitName");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([{ field: "unitName", sort: "asc" }]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [settingModalOpen, setSettingModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [dependencies, setDependencies] = useState(null);

  const searchByOptions = [{ value: "unitName", label: "Unit Name" }];
  const searchByLabel =
    searchByOptions.find((o) => o.value === searchBy)?.label || "Unit Name";

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };

  const handleSettingModalOpen = () => {
    setSettingModalOpen(true);
  };

  const handleSettingModalClose = () => {
    setSettingModalOpen(false);
  };

  const handleEdit = (rowData) => {
    const original = rowData._original || rowData;
    setSelectedRow(original);
    handleModalOpen();
  };

  const handleDeleteClick = (row) => {
    const original = row._original || row;
    setItemToDelete(original);
    setDependencies(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setLoading(true);
    const response = await deleteUnitByIdApi(itemToDelete._id);
    setLoading(false);
    if (response?.code === 200) {
      toast.success(response?.message || "Unit deleted successfully");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDependencies(null);
      refetch();
    } else if (response?.code === 400 && response?.dependencies) {
      setDependencies(response.dependencies);
      toast.warning(response?.message || "Cannot delete due to dependencies");
    } else {
      toast.error(response?.message || "Failed to delete unit");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setDependencies(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
    setDependencies(null);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const getColumns = useCallback(() => {
    return [
      {
        field: "unitName",
        headerName: "Unit Name",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: true,
        renderCell: (params) => (
          <Chip
            label={params.value || "Active"}
            color={params.value === "Inactive" ? "error" : "success"}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "receivingTransferApproval",
        headerName: "Receiving",
        flex: 0.8,
        minWidth: 100,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "teleconsultActionMode",
        headerName: "Teleconsult-Action Mode",
        flex: 1,
        minWidth: 140,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "tallyAccountName",
        headerName: "Tally Account",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 120,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => {
          const row = params.row._original || params.row;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <EditIcon
                sx={{
                  color: "transparent",
                  fontSize: "18px",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(params.row);
                }}
              />
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSettingModalOpen();
                }}
                sx={{ p: 0.25 }}
              >
                <SettingsOutlined sx={{ cursor: "pointer", fontSize: "18px" }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(params.row);
                }}
                sx={{ p: 0.25 }}
              >
                <Close
                  sx={{
                    cursor: "pointer",
                    fontSize: "18px",
                    color: "error.main",
                  }}
                />
              </IconButton>
            </Box>
          );
        },
      },
    ];
  }, []);

  const {
    dataList: unitList,
    dataCount: unitCount,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getUnitApi,
    params: {
      limit: paginationModel.pageSize,
      page: paginationModel.page + 1,
      search,
      searchBy,
      sort: sortModel[0]?.field || "unitName",
      order: sortModel[0]?.sort || "asc",
    },
    dependencies: [
      search,
      searchBy,
      paginationModel.pageSize,
      paginationModel.page,
      sortModel,
    ],
    debounceDelay: 500,
    dataKey: "units",
    countKey: "total",
  });

  const rawRows =
    unitList?.map((row) => ({
      id: row._id,
      _id: row._id,
      unitName: row?.unitName || "-",
      status: row?.active ? "Active" : "Inactive",
      receivingTransferApproval: row?.receivingTransferApproval ?? "-",
      teleconsultActionMode: row?.teleconsultActionMode ?? "-",
      tallyAccountName: row?.tallyAccountName ?? "-",
      _original: row,
    })) || [];

  const sortField = sortModel[0]?.field || "unitName";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = [...rawRows].sort((a, b) => {
    const aVal = a[sortField] ?? "";
    const bVal = b[sortField] ?? "";
    const cmp = String(aVal).localeCompare(String(bVal), undefined, {
      numeric: true,
    });
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;
  const rows =
    sortedRows.length > pageSize
      ? sortedRows.slice(page * pageSize, page * pageSize + pageSize)
      : sortedRows;

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>

        <Box sx={{ width: "100%", pr: "90px", paddingBottom: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#5D6A82",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: "bold",
              padding: 0,
              transition: "none !important",
              mt: 2,
            }}
          >
            Back
          </Button>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 2,
            }}
          >
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
              Unit Master
            </Typography>
          </Box>
          <Box
            sx={{
              my: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 2,
            }}
          >
            <TextField
              placeholder={`Search by ${searchByLabel}`}
              size="small"
              sx={{ width: "300px" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <select
                        className="uid-select"
                        value={searchBy}
                        onChange={(e) => setSearchBy(e.target.value)}
                        style={{
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {searchByOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              value={search || ""}
              onChange={(event) => setSearch(event.target.value || "")}
            />

            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                display: "flex",
                gap: 0.5,
                minWidth: "150px",
              }}
              onClick={handleModalOpen}
            >
              <AddCircleOutlineOutlined />
              New Unit
            </Button>
          </Box>

          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box
              sx={{
                height: {
                  xs: "calc(100vh - 300px)",
                  sm: "calc(100vh - 320px)",
                  md: "calc(100vh - 350px)",
                },
                width: "100%",
                minHeight: "300px",
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                paginationMode="server"
                rowCount={unitCount}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                loading={isLoading}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-root": {
                    border: "none",
                    width: "100%",
                  },
                  "& .MuiDataGrid-main": {
                    width: "100%",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #f0f0f0",
                    padding: isMobile ? "4px" : "8px",
                    fontSize: isMobile ? "12px" : "14px",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f5f5f5",
                    borderBottom: "2px solid #e0e0e0",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold",
                    fontSize: isMobile ? "12px" : "14px",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "bold",
                    fontSize: isMobile ? "12px" : "14px",
                  },
                  "& .MuiDataGrid-row": {
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      cursor: "pointer",
                    },
                    "&:nth-of-type(odd)": {
                      backgroundColor: "#fafafa",
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: "white",
                    },
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "2px solid #e0e0e0",
                    backgroundColor: "#f9f9f9",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    minHeight: "200px",
                  },
                  [theme.breakpoints.down("sm")]: {
                    "& .MuiDataGrid-columnHeader": {
                      fontSize: "11px",
                      padding: "4px",
                    },
                    "& .MuiDataGrid-cell": {
                      fontSize: "11px",
                      padding: "2px 4px",
                    },
                  },
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No units found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}

          <AddUnitModal
            open={modalOpen}
            onClose={handleModalClose}
            refetch={refetch}
            selectedRow={selectedRow}
          />
          <UnitSettingModal
            open={settingModalOpen}
            onClose={handleSettingModalClose}
          />
          <DeleteConfirmationDialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Unit"
            itemName={itemToDelete ? `"${itemToDelete.unitName}"` : "this unit"}
            dependencies={dependencies}
            isLoading={loading}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default MasterUnit;
